import {isOutsideTimeWindow} from './slides';

// '' is before '*' in the sort order
const sortEmptyStringsFirst = true;

// multi-value cells arrive as a sorted JSON array string, e.g. '["2","12","101"]'
// (see stringifyLandmarkID in vulture) — those rows sort by their first (lowest)
// element. Cheap prefix extraction instead of JSON.parse: the comparator runs
// O(n log n) and the values never contain quotes. Non-string values (rank is a
// number) pass through untouched.
export const firstJSONArrayElement = function(val) {
	if (typeof val !== 'string' || !val.startsWith('["')) {
		return val;
	}

	const end = val.indexOf('"', 2);

	return end === -1 ? val.slice(2) : val.slice(2, end);
};

export const compareEmptyFirst = function(aVal, bVal) {
	if (!sortEmptyStringsFirst) {
		return 0;
	}

	if (aVal === '' && bVal !== '') {
		return -1;
	}

	if (aVal !== '' && bVal === '') {
		return 1;
	}

	return 0;
};

export const compareListingValues = function(aRaw, bRaw, dir) {
	let aVal = firstJSONArrayElement(aRaw || '');
	let bVal = firstJSONArrayElement(bRaw || '');

	const emptyOrder = compareEmptyFirst(aVal, bVal);

	if (emptyOrder !== 0) {
		return emptyOrder;
	}

	const isNumber = !isNaN(parseFloat(aVal)) && isFinite(aVal) && !isNaN(parseFloat(bVal)) && isFinite(bVal);

	aVal = isNumber ? parseFloat(aVal) : String(aVal).toLowerCase();
	bVal = isNumber ? parseFloat(bVal) : String(bVal).toLowerCase();

	const sign = dir === -1 ? -1 : 1;

	if (aVal < bVal) {
		return -1 * sign;
	}

	if (aVal > bVal) {
		return 1 * sign;
	}

	return 0;
};

// a/b.sortedVals = [rank, col1, col2, col3, ...] in the proper order
// if this is provided, it will multi-sort by the columns in order
// sortedDirs pairs with sortedVals: 1 = ASC, -1 = DESC, omitted = all ASC.
export const sortListingsByCols = function(a, b, sortedDirs) {
	if (!sortedDirs) {
		sortedDirs = a.sortedDirs || b.sortedDirs;
	}

	// when listings are filtered(searched) they come through as arrays
	// the last element in the array is the sortedVals array, and we just
	// create the object here to make it easier to work with
	if (Array.isArray(a) && Array.isArray(b)) {
		a = {sortedVals: a[a.length - 1]};
		b = {sortedVals: b[b.length - 1]};
	}

	for (let i = 0; i < a.sortedVals.length; i++) {
		const order = compareListingValues(a.sortedVals[i], b.sortedVals[i], (sortedDirs || [])[i]);

		if (order !== 0) {
			return order;
		}
	}

	return 0;
};

export const makeSortedDirs = function(sortedCols) {
	return [1, ...sortedCols.map((col) => {
		return col.sort_direction === 1 ? -1 : 1;
	})];
};

/**
 * Sorts an array of numerical values and returns an array of indices indicating
 * the original positions of each value in sorted order.
 *
 * For example, given the input array `[2, 3, 0]`, the function will return `[3, 1, 2]`,
 * meaning that `0` (smallest value) was originally at index 3, `2` (second smallest) was
 * at index 1, and `3` (largest) was at index 2.
 *
 * This function is useful when you want to identify the original indices of the sorted elements
 * instead of sorting the array directly.
 *
 * @param {number[]} arr - An array of numbers to be sorted and analyzed.
 * @returns {number[]} - An array of indices representing the original positions of each sorted value.
 */
export const makeSortValues = function(arr) {
	const indexedValues = arr.map((value, index) => {
		return {index: index, value: value};
	});

	indexedValues.sort((a, b) => {
		return a.value - b.value;
	});

	const sortedIndices = indexedValues.map((pair) => {
		return pair.index + 1;
	});

	return sortedIndices;
};

export const makeSortKeyIndices = function(sortValues) {
	const sortable = (sortValues || [])
		.map((sort, index) => {
			return {sort: sort, index: index};
		})
		.filter((col) => {
			return col.sort > 0;
		});

	return makeSortValues(sortable.map((col) => {
		return col.sort;
	})).map((position) => {
		return sortable[position - 1].index + 1;
	});
};

export const makeSortedDirsForIndices = function(sortValues, sortDirections) {
	return [1, ...sortValues.map((sortIndex) => {
		return (sortDirections || [])[sortIndex - 1] === 1 ? -1 : 1;
	})];
};

// One cell per ETL column, in colGroups order; matchListingRow reads these.
export const buildSearchCells = function(item) {
	return item.vals.map((listingData, i) => ({
		value:             listingData,
		type:              item.types[i],
		displayName:       item.displayNames[i],
		column_uuid:       item.colGroups[i].m1Key,
		excludeFromSearch: item.colGroups[i].excludeFromSearch,
	}));
};

// Stop words list (can be expanded / externalized later)
const searchStopWords = new Set(['the','a','an','and','or','but','of','for','to','in','on','at','by','from','with','without','over','under']);

// listingRow = [m1Key, ...cells, tierConfig, detailsGrp] (see Listings.getCompleteListings).
// 'primary' = the whole input appears in a searchable cell; 'secondary' = a meaningful word
// appears in one of the tier's additional search fields; null = no match.
// Cells flagged excludeFromSearch still render but are never matched.
export const matchListingRow = function(listingRow, value) {
	const lower = value.toLowerCase();
	const tierConfig = listingRow[listingRow.length - 2] || {};
	const extraVals = Array.isArray(tierConfig.additionalSearchValues) ? tierConfig.additionalSearchValues : [];
	const visibleVals = listingRow
		.slice(1, listingRow.length - 2)
		.filter((el) => !el?.excludeFromSearch)
		.map((el) => (el?.value ?? ''))
		.filter(Boolean);

	const visibleHaystack = visibleVals.map((v) => (v ?? '').toString().toLowerCase());
	if (visibleHaystack.some((s) => s.includes(lower))) {
		return 'primary';
	}

	const extraHaystack = extraVals.map((v) => (v ?? '').toString().toLowerCase());
	const meaningfulTokens = lower.split(/\s+/).filter(Boolean).filter((t) => !searchStopWords.has(t));
	if (meaningfulTokens.some((tok) => extraHaystack.some((field) => field.includes(tok)))) {
		return 'secondary';
	}

	return null;
};

export const getColumnsAndListings = function(tmpObj, isCombined = false, tierColGroups = []) {
	let listings = [];

	// Don't mutate props/state
	let obj = JSON.parse(JSON.stringify(tmpObj));

	if (obj.listings) {
		obj.listings.forEach((listing) => {
			// Only override tierColGroups if there is a length of listing.colGroups
			if (listing.colGroups.length > 0) {
				tierColGroups = listing.colGroups;
				listings.push(listing);

				if (isCombined) {
					(listing.listings || []).forEach((childListing) => {
						childListing.colGroups = tierColGroups;
						listings.push(childListing);
					});
					listing.listings = [];
				}
			} else {
				let [childColGroups, childListings] = getColumnsAndListings(listing, false, tierColGroups);
				tierColGroups = childColGroups;
				listings.push(...childListings);
			}
		});
	}

	let sortedCols = [...tierColGroups].filter((c) => {
		if (c.sort > 0) {
			return true;
		}

		return false;
	});

	sortedCols.sort((i, j) => {
		if (i.sort > j.sort) {
			return 1;
		}

		return -1;
	});

	const sortedDirs = makeSortedDirs(sortedCols);

	listings = listings.map((listing) => {
		listing.sortedVals = [listing.rank || 0];

		for (let col of sortedCols) {
			listing.sortedVals.push(listing.vals[col.order - 1]);
		}

		listing.sortedDirs = sortedDirs;

		return listing;
	});

	listings.sort((a, b) => {
		return sortListingsByCols(a, b, sortedDirs);
	});

	return [tierColGroups, listings];
};

export const getLowestSortValue = (colGroups) => {
	if (!Array.isArray(colGroups) || colGroups.length === 0) {
		return [0, 0]; // or other appropriate default values
	}

	let index = 0;

	const lowestSortValue = colGroups.reduce((lowestValueYet, currentValue, i) => {
		if (currentValue?.sort === 0) {
			return lowestValueYet;
		}

		if (currentValue?.sort < lowestValueYet) {
			index = i;
			return currentValue.sort;
		}

		return lowestValueYet;
	}, Infinity);

	if (lowestSortValue === Infinity) {
		return [0, 0];
	}

	return [lowestSortValue, index];
};

/**
 * Determines whether scheduling-based filtering is enabled for a listing config.
 * Walks flatData to find the listing_scheduling_toggle setting applied to the
 * listing_config. Groups with the toggle off should not be filtered by
 * shouldShowListing.
 *
 * @param {Object} flatData - The window.flatData map
 * @param {string} listingConfigUUID - The listing config's m1Key
 * @returns {boolean} - true if scheduling filtering is enabled for this group
 */
export const isListingSchedulingOn = (flatData, listingConfigUUID) => {
	let listingSchedulingToggleSetting;
	let isFilteringOn = false;

	for (const m1Key in flatData) {
		if (flatData[m1Key]?.name === 'listing_scheduling_toggle') {
			listingSchedulingToggleSetting = m1Key;
		}

		if (listingSchedulingToggleSetting) {
			break;
		}
	}

	if (!listingSchedulingToggleSetting) {
		return false;
	}

	if (listingConfigUUID) {
		for (const m1Key in flatData[listingConfigUUID]) {
			if (m1Key === listingSchedulingToggleSetting) {
				isFilteringOn = !!flatData[listingConfigUUID][m1Key]?.bool;
				break;
			}
		}
	}

	return isFilteringOn;
};

/**
 * Determines whether a listing should be shown based on its scheduling config.
 * Evaluates both the new format ({ scheduled, scheduling: {...} }) and the
 * legacy format ({ scheduling (boolean), use_dates, use_times, use_days, ... }).
 *
 * @param {string|undefined} schedulingJSON - The raw scheduling JSON string (typically from window.flatData[m1Key].listing_scheduling)
 * @returns {boolean} - true if the listing should be shown, false if it should be filtered out
 */
export const shouldShowListing = (schedulingJSON) => {
	if (schedulingJSON === undefined) {
		return true;
	}

	let parsed;
	try {
		parsed = JSON.parse(schedulingJSON);
	} catch (err) {
		console.error('Failed to parse schedulingJSON', err);
		return true;
	}

	// New format: { scheduled, scheduling: {...}, schedulingMode }
	if (parsed.scheduled !== undefined) {
		if (!parsed.scheduled) {
			return true;
		}

		const scheduling = parsed.scheduling;
		if (!scheduling) {
			return true;
		}

		const now = new Date();

		if (scheduling.start_date) {
			const startDate = new Date(scheduling.start_date);
			if (now < startDate) {
				return false;
			}
		}

		if (scheduling.end_date) {
			const endDate = new Date(scheduling.end_date);
			if (now > endDate) {
				return false;
			}
		}

		if (scheduling.recurring_start_time || scheduling.recurring_end_time) {
			if (isOutsideTimeWindow(now, scheduling.recurring_start_time, scheduling.recurring_end_time)) {
				return false;
			}
		}

		if (scheduling.days && Array.isArray(scheduling.days) && scheduling.days.length > 0) {
			const today = now.getDay();
			if (!scheduling.days.includes(today)) {
				return false;
			}
		}

		if (scheduling.recurring_times_by_day && Object.keys(scheduling.recurring_times_by_day).length > 0) {
			const today = now.getDay();
			const todaySchedule = scheduling.recurring_times_by_day[today] || scheduling.recurring_times_by_day[String(today)];

			if (!todaySchedule) {
				return false;
			}

			if (todaySchedule.use_recurring_times === false) {
				return true;
			}

			if (todaySchedule.use_recurring_times && (todaySchedule.start_time || todaySchedule.end_time)) {
				if (isOutsideTimeWindow(now, todaySchedule.start_time, todaySchedule.end_time)) {
					return false;
				}
			}
		}

		return true;
	}

	// Legacy format
	if (!parsed.scheduling) {
		return true;
	}

	if (parsed.use_dates) {
		if (parsed.start_date) {
			const now = new Date().toISOString();
			if (now < parsed.start_date) {
				return false;
			}
		}

		// NOTE: The end_date is supposed to represent the final day a slide will show. The date a slide expires is the next day.
		if (parsed.end_date) {
			const now = new Date().toISOString();
			const endDate = new Date(parsed.end_date);
			let dateToExpire = endDate.setDate(endDate.getDate() + 1);
			dateToExpire = new Date(dateToExpire).toISOString();
			if (now > dateToExpire) {
				return false;
			}
		}
	}

	if (parsed.use_times) {
		const now = new Date().toTimeString().slice(0, 8);

		if (parsed.start_time) {
			let startTime = parsed.start_time;
			if (startTime.length === 7) {
				startTime = '0' + startTime;
			}
			if (now < startTime) {
				return false;
			}
		}

		if (parsed.end_time) {
			let endTime = parsed.end_time;
			if (endTime.length === 7) {
				endTime = '0' + endTime;
			}
			if (now > endTime) {
				return false;
			}
		}
	}

	if (parsed.use_days) {
		const today = new Date().getDay();
		const days = parsed.days;
		if (days && Array.isArray(days) && !~days.indexOf(today)) {
			return false;
		}
	}

	return true;
};
